import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Building2, Box, Users, FileText, FolderOpen, Save, X, MapPin, Clock } from 'lucide-react';
import { BuildingPassportTab } from './BuildingPassportTab';
import { UnitsTab } from './UnitsTab';
import { TenantsTab } from './TenantsTab';
import { LeasesTab } from './LeasesTab';
import { DocumentsTab } from './DocumentsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BuildingDetailDialog = ({ 
  building, 
  isOpen, 
  onClose, 
  onSave,
  t 
}) => {
  const [activeTab, setActiveTab] = useState('passport');
  const [formData, setFormData] = useState(building || {});
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [wale, setWale] = useState(null);
  const [completeness, setCompleteness] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchCompleteness = useCallback(async () => {
    if (!building?.realestate_id) return;
    try {
      
      const res = await axios.get(`${API_URL}/api/buildings/${building.realestate_id}/completeness`, {
        withCredentials: true
      });
      setCompleteness(res.data.vt15_completeness || 0);
    } catch (err) {
      console.error('Error fetching completeness:', err);
    }
  }, [building?.realestate_id]);

  const fetchWale = useCallback(async () => {
    if (!building?.realestate_id) return;
    try {
      
      const res = await axios.get(`${API_URL}/api/buildings/${building.realestate_id}/wale`, {
        withCredentials: true
      });
      setWale(res.data);
    } catch (err) {
      console.error('Error fetching WALE:', err);
    }
  }, [building?.realestate_id]);

  useEffect(() => {
    if (isOpen && building) {
      setFormData(building);
      setActiveTab('passport');
      setHasChanges(false);
      fetchCompleteness();
      fetchWale();
    }
  }, [isOpen, building, fetchCompleteness, fetchWale]);

  const handlePassportUpdate = (updatedData) => {
    setFormData(prev => ({ ...prev, ...updatedData }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      
      await axios.put(`${API_URL}/api/realestate/${building.realestate_id}`, formData, {
        withCredentials: true
      });
      toast.success('Gebouw opgeslagen');
      setHasChanges(false);
      if (onSave) onSave(formData);
      fetchCompleteness();
    } catch (err) {
      console.error('Error saving building:', err);
      toast.error('Fout bij opslaan gebouw');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnitsChange = (newUnits) => {
    setUnits(newUnits);
    fetchWale();
  };

  const handleTenantsChange = (newTenants) => {
    setTenants(newTenants);
  };

  const handleLeasesChange = (newLeases) => {
    setLeases(newLeases);
    fetchWale();
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('Je hebt onopgeslagen wijzigingen. Weet je zeker dat je wilt sluiten?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  if (!building) return null;

  const tabConfig = [
    { id: 'passport', label: 'Gebouwenpaspoort', icon: Building2, count: `${completeness}%` },
    { id: 'units', label: 'Units', icon: Box, count: units.length },
    { id: 'tenants', label: 'Huurders', icon: Users, count: tenants.length },
    { id: 'leases', label: 'Huurcontracten', icon: FileText, count: leases.length },
    { id: 'documents', label: 'Documenten', icon: FolderOpen, count: 0 }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-6xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="building-detail-dialog">
        <DialogHeader className="flex-shrink-0 border-b border-slate-700 pb-4">
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl text-white flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#b4976f]" />
                {building.address}
              </DialogTitle>
              <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {building.postal_code} {building.city}
                </span>
                {wale?.wale_years && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    WALE: {wale.wale_years} jaar
                  </span>
                )}
                <Badge 
                  className={`${
                    completeness >= 70 ? 'bg-green-600' : 
                    completeness >= 40 ? 'bg-yellow-600' : 'bg-red-600'
                  }`}
                >
                  {completeness}% compleet
                </Badge>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasChanges && (
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-[#b4976f] hover:bg-[#a3865f] text-white"
                  data-testid="save-building-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Opslaan...' : 'Opslaan'}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="flex-shrink-0 bg-slate-800/50 border-b border-slate-700 rounded-none justify-start px-2 h-auto py-1 gap-1">
            {tabConfig.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="data-[state=active]:bg-[#b4976f] data-[state=active]:text-white text-slate-400 rounded-lg px-4 py-2"
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
                {tab.count !== undefined && (
                  <Badge variant="secondary" className="ml-2 bg-slate-700 text-white text-xs">
                    {tab.count}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex-1 overflow-y-auto p-4">
            <TabsContent value="passport" className="mt-0 h-full">
              <BuildingPassportTab
                building={formData}
                onUpdate={handlePassportUpdate}
                completeness={completeness}
                t={t}
              />
            </TabsContent>

            <TabsContent value="units" className="mt-0 h-full">
              <UnitsTab
                buildingId={building.realestate_id}
                onUnitsChange={handleUnitsChange}
                t={t}
              />
            </TabsContent>

            <TabsContent value="tenants" className="mt-0 h-full">
              <TenantsTab
                buildingId={building.realestate_id}
                onTenantsChange={handleTenantsChange}
                t={t}
              />
            </TabsContent>

            <TabsContent value="leases" className="mt-0 h-full">
              <LeasesTab
                buildingId={building.realestate_id}
                units={units}
                tenants={tenants}
                wale={wale}
                onLeasesChange={handleLeasesChange}
                t={t}
              />
            </TabsContent>

            <TabsContent value="documents" className="mt-0 h-full">
              <DocumentsTab
                buildingId={building.realestate_id}
                t={t}
              />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default BuildingDetailDialog;
